import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { toast } from 'react-toastify'
import {
  HelpCircle,
  Plus,
  X,
  RotateCcw,
  Save,
  Edit2,
  Check,
  AlertCircle,
  Info
} from 'lucide-react'

interface BrandQuestionsCardProps {
  brandId: string
}

export default function BrandQuestionsCard({ brandId }: BrandQuestionsCardProps) {
  const [questions, setQuestions] = useState<string[]>([])
  const [isOverride, setIsOverride] = useState(false)
  const [canRevert, setCanRevert] = useState(false)
  const [generatedAt, setGeneratedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedQuestions, setEditedQuestions] = useState<string[]>([])
  const [newQuestion, setNewQuestion] = useState('')
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    fetchQuestions()
  }, [brandId])

  const fetchQuestions = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/llms/${brandId}/questions`)
      if (response.data.success) {
        setQuestions(response.data.questions || [])
        setIsOverride(response.data.isOverride)
        setCanRevert(response.data.canRevert)
        setGeneratedAt(response.data.brandQuestionsGeneratedAt)
        setEditedQuestions(response.data.questions || [])
      }
    } catch (error) {
      console.error('Failed to fetch questions:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleStartEdit = () => {
    setEditedQuestions([...questions])
    setIsEditing(true)
    setHasChanges(false)
  }

  const handleCancelEdit = () => {
    setEditedQuestions([...questions])
    setIsEditing(false)
    setHasChanges(false)
    setNewQuestion('')
  }

  const handleAddQuestion = () => {
    if (!newQuestion.trim()) return

    let cleaned = newQuestion.trim()
    if (!cleaned.endsWith('?')) cleaned += '?'
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1)

    setEditedQuestions([...editedQuestions, cleaned])
    setNewQuestion('')
    setHasChanges(true)
  }

  const handleRemoveQuestion = (index: number) => {
    const updated = editedQuestions.filter((_, i) => i !== index)
    setEditedQuestions(updated)
    setHasChanges(true)
  }

  const handleEditQuestion = (index: number, value: string) => {
    const updated = [...editedQuestions]
    updated[index] = value
    setEditedQuestions(updated)
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const response = await api.put(`/llms/${brandId}/questions`, {
        questions: editedQuestions
      })

      if (response.data.success) {
        setQuestions(response.data.questions)
        setIsOverride(true)
        setCanRevert(true)
        setIsEditing(false)
        setHasChanges(false)
        toast.success('Questions saved successfully')
      }
    } catch (error) {
      console.error('Failed to save questions:', error)
      toast.error('Failed to save questions')
    } finally {
      setSaving(false)
    }
  }

  const handleRevert = async () => {
    if (!window.confirm('Revert to the original AI-generated questions? Your custom questions will be removed.')) {
      return
    }

    try {
      setSaving(true)
      const response = await api.delete(`/llms/${brandId}/questions`)

      if (response.data.success) {
        setQuestions(response.data.questions)
        setEditedQuestions(response.data.questions)
        setIsOverride(false)
        setCanRevert(false)
        setIsEditing(false)
        setHasChanges(false)
        toast.success('Reverted to original questions')
      }
    } catch (error) {
      console.error('Failed to revert questions:', error)
      toast.error('Failed to revert questions')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Questions We Answer</h3>
          {isOverride && (
            <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
              Custom
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canRevert && !isEditing && (
            <button
              onClick={handleRevert}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Revert to AI-generated questions"
            >
              <RotateCcw className="w-4 h-4" />
              Revert
            </button>
          )}
          {!isEditing ? (
            <button
              onClick={handleStartEdit}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 rounded-lg transition-colors"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save
              </button>
            </>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-500 mb-4">
        These are the consumer questions your brand can answer. They appear in your LLMS.txt to help AI assistants understand what queries your products address.
      </p>

      {/* Info banner for override */}
      {isOverride && !isEditing && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            You're using custom questions. Click "Revert" to restore the AI-generated questions.
          </p>
        </div>
      )}

      {/* Empty state */}
      {questions.length === 0 && !isEditing && (
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">No questions generated yet</p>
          <p className="text-sm text-gray-400">
            Questions are automatically generated during scheduled analysis based on your products and search queries.
          </p>
          <button
            onClick={handleStartEdit}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add questions manually
          </button>
        </div>
      )}

      {/* Questions list */}
      {(questions.length > 0 || isEditing) && (
        <div className="space-y-2">
          {(isEditing ? editedQuestions : questions).map((question, index) => (
            <div
              key={index}
              className={`flex items-center gap-2 p-3 rounded-lg ${
                isEditing ? 'bg-gray-50 border border-gray-200' : 'bg-gray-50'
              }`}
            >
              <span className="text-indigo-500 font-medium text-sm">{index + 1}.</span>
              {isEditing ? (
                <input
                  type="text"
                  value={question}
                  onChange={(e) => handleEditQuestion(index, e.target.value)}
                  className="flex-1 bg-white border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              ) : (
                <span className="flex-1 text-sm text-gray-700">{question}</span>
              )}
              {isEditing && (
                <button
                  onClick={() => handleRemoveQuestion(index)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  title="Remove question"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}

          {/* Add new question input */}
          {isEditing && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
              <Plus className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddQuestion()
                  }
                }}
                placeholder="Add a new question..."
                className="flex-1 bg-transparent border-none text-sm focus:outline-none placeholder-gray-400"
              />
              <button
                onClick={handleAddQuestion}
                disabled={!newQuestion.trim()}
                className="p-1 text-indigo-600 hover:text-indigo-700 disabled:text-gray-300 transition-colors"
                title="Add question"
              >
                <Check className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Footer with generation info */}
      {generatedAt && !isOverride && (
        <p className="text-xs text-gray-400 mt-4">
          AI-generated {new Date(generatedAt).toLocaleDateString()}
        </p>
      )}
    </div>
  )
}
